/**
 * menuData — the single navigation reference for RentalOS.
 *
 * Records, for every page in the design's menu, its **page id**, **slug**, and
 * **page name** (plus icon + module id). The layout's navigation is driven from
 * this file and routes by **slug** — never by page id. Keep it in sync as pages
 * are added/renamed; do not hand-maintain the menu inline in the layout.
 *
 * Sourced from RawHTMLDesign layout `menuData` + PageDesignMap.json (page ids)
 * + the design folder leaf (slug).
 */

export const APP_NAME = 'RentalOS';

// The default landing page (is_default_page === true in scope).
export const DEFAULT_SLUG = 'login-portal-selector';

/** Every business role in scope — used for pages any signed-in role may reach. */
export const ALL_ROLES = [
  'portfolio_director',
  'asset_manager',
  'cfo',
  'compliance_officer',
  'leasing_agent',
  'owner_principal',
  'xamun_ops',
  'tenant',
];

/**
 * Nav groups — functional sub-menus that keep the top bar uncrowded for the
 * senior roles (a Portfolio Director otherwise sees 10 flat links). A group
 * only renders when the role can see at least one of its items, and a group
 * left with a single visible item collapses to a plain top-level link (so
 * e.g. Xamun Ops gets a direct "Integration Management Hub" link, not a
 * one-entry dropdown).
 */
export const MENU_GROUPS = [
  { key: 'renewals', label: 'Renewals', icon: 'fas fa-sync-alt' },
  { key: 'governance', label: 'Governance', icon: 'fas fa-shield-alt' },
  { key: 'admin', label: 'Admin', icon: 'fas fa-cogs' },
];

/**
 * @typedef {Object} MenuItem
 * @property {string} pageId   Canonical page id (UUID) from PageDesignMap.
 * @property {string} slug     Slug-based route key (folder leaf).
 * @property {string} name     Human-readable menu label.
 * @property {string} icon     FontAwesome icon class.
 * @property {number} moduleId Design module id the page belongs to.
 * @property {string} [group]  MENU_GROUPS key; ungrouped items stay top-level.
 * @property {string} path     Route path (`/<slug>`).
 */

/** @type {MenuItem[]} */
export const menuItems = [
  {
    pageId: '656a3966-ce49-4e23-8535-4faa9ec4661b',
    slug: 'login-portal-selector',
    name: 'Portal Home',
    icon: 'fas fa-door-open',
    moduleId: 22342,
    roles: ALL_ROLES,
  },
  {
    pageId: 'd428f571-6e71-456e-8163-cc1443fe2a4c',
    slug: 'portfolio-director-dashboard',
    name: 'Portfolio Director Dashboard',
    icon: 'fas fa-chart-line',
    moduleId: 22342,
    roles: ['portfolio_director', 'asset_manager', 'cfo', 'compliance_officer'],
  },
  {
    pageId: 'c5e4cf57-2cc2-447a-b28f-d8288e7ecac2',
    slug: 'rera-compliance-engine',
    name: 'RERA Compliance Engine',
    icon: 'fas fa-gavel',
    moduleId: 22343,
    group: 'governance',
    roles: ['compliance_officer', 'portfolio_director'],
  },
  {
    pageId: 'f95ea50c-d8f5-4f0a-89fd-336c13b0ea11',
    slug: 'renewal-radar',
    name: 'Renewal Radar',
    icon: 'fas fa-hourglass-half',
    moduleId: 22344,
    group: 'renewals',
    roles: ['leasing_agent', 'asset_manager', 'portfolio_director'],
  },
  {
    pageId: 'eb2f21a1-15e7-478e-b7f2-f2efd74cf440',
    slug: 'leasing-agent-renewal-desk',
    name: 'Leasing Agent Renewal Desk',
    icon: 'fas fa-user-tie',
    moduleId: 22344,
    group: 'renewals',
    roles: ['leasing_agent', 'asset_manager', 'portfolio_director'],
  },
  {
    pageId: 'ed6dd682-de46-4a41-8082-bdfd066c1c4f',
    slug: 'yield-optimiser',
    name: 'Yield Optimiser',
    icon: 'fas fa-calculator',
    moduleId: 22345,
    group: 'renewals',
    roles: ['leasing_agent', 'asset_manager', 'portfolio_director'],
  },
  {
    pageId: '8950f269-9465-4357-9534-4223ea2477d3',
    slug: 'notice-communication-studio',
    name: 'Notice & Communication Studio',
    icon: 'fas fa-file-signature',
    moduleId: 22346,
    group: 'renewals',
    roles: ['leasing_agent', 'asset_manager', 'portfolio_director'],
  },
  {
    pageId: '0347d42a-6296-4e65-a192-8c88aa6d7ea4',
    slug: 'owner-governance-reports',
    name: 'Owner Governance & Reports',
    icon: 'fas fa-landmark',
    moduleId: 22347,
    group: 'governance',
    roles: ['asset_manager', 'portfolio_director', 'owner_principal'],
  },
  {
    pageId: 'a79840d2-a955-4822-b17a-d64c0ec7eb67',
    slug: 'baseline-outcome-ledger',
    name: 'Baseline & Outcome Ledger',
    icon: 'fas fa-coins',
    moduleId: 22348,
    group: 'governance',
    roles: ['cfo', 'portfolio_director'],
  },
  {
    pageId: '00bf8371-55bd-4d93-9eb1-46b5d168c8c3',
    slug: 'integration-management-hub',
    name: 'Integration Management Hub',
    icon: 'fas fa-plug',
    moduleId: 22349,
    group: 'admin',
    roles: ['portfolio_director', 'cfo', 'xamun_ops', 'asset_manager'],
  },
  {
    pageId: '0ba44b87-0c13-4971-8e4f-3891923afdc3',
    slug: 'tenant-renewal-portal',
    name: 'Tenant Renewal Portal',
    icon: 'fas fa-home-user',
    moduleId: 22350,
    roles: ['tenant'],
  },
].map((item) => ({ ...item, path: `/${item.slug}` }));

/**
 * Menu items visible to a given business role — the nav mirrors each page's
 * RoleRoute guard so a rendered link never lands the user on a 403. Anonymous
 * users (no role) get no page links.
 */
export function menuItemsForRole(businessRole) {
  if (!businessRole) return [];
  return menuItems.filter((item) => item.roles.includes(businessRole));
}

/**
 * Role-filtered nav tree: top-level links plus MENU_GROUPS dropdowns, in the
 * original menu order (a group anchors where its first visible item sat).
 * Groups with no visible items are dropped; a group with exactly one visible
 * item collapses to a plain top-level link (see MENU_GROUPS note).
 *
 * @returns {Array<{type:'link', item:MenuItem} |
 *                 {type:'group', key:string, label:string, icon:string, items:MenuItem[]}>}
 */
export function menuTreeForRole(businessRole) {
  const entries = [];
  const groupEntries = new Map();

  for (const item of menuItemsForRole(businessRole)) {
    const groupDef = item.group && MENU_GROUPS.find((g) => g.key === item.group);
    if (!groupDef) {
      entries.push({ type: 'link', item });
      continue;
    }
    let groupEntry = groupEntries.get(groupDef.key);
    if (!groupEntry) {
      groupEntry = { type: 'group', ...groupDef, items: [] };
      groupEntries.set(groupDef.key, groupEntry);
      entries.push(groupEntry);
    }
    groupEntry.items.push(item);
  }

  return entries.map((entry) =>
    entry.type === 'group' && entry.items.length === 1 ? { type: 'link', item: entry.items[0] } : entry
  );
}

/**
 * Slugs whose real page component has been generated. As
 * ui-components-from-reference builds each page, add its slug here so the nav
 * links to the real route; until then the layout points the link at the 404
 * page so navigation never dead-ends.
 */
export const implementedSlugs = new Set([
  'login-portal-selector',
  'portfolio-director-dashboard',
  'rera-compliance-engine',
  'renewal-radar',
  'leasing-agent-renewal-desk',
  'owner-governance-reports',
  'yield-optimiser',
  'integration-management-hub',
  'notice-communication-studio',
  'tenant-renewal-portal',
  'baseline-outcome-ledger',
]);

export function isImplemented(slug) {
  return implementedSlugs.has(slug);
}

export default menuItems;
